import { SORT_BY_ROOM } from "@/components/shared/util";
import { convertServerDataPoints } from "@/filters/DateAndTime";
import {
  LocationType,
  PopupDescription,
  RiskFactor,
  RootState,
  VarLevel
} from "@/store/types";
import _ from "lodash";
import { GetterTree } from "vuex";
import { ViewConfig } from "./config";
import { Patient, PatientState } from "./types";

export function patientHasRisk(patient: Patient, risk: string) {
  return patient.body.risk.includes(risk);
}

export function createInfusions(patient: Patient) {
  const status: Record<string, any> = {};
  if (patient && patient.body && patient.body.atoms) {
    const atoms = patient.body.atoms;
    const infusions = atoms.infusions;
    if (!infusions) {
      return status;
    }
    const list = infusions.legend.split("|");

    list.forEach((listItem: any) => {
      const attr = listItem.split(",");
      if (attr.length != 3)
        // TODO: Log data error
        return;

      // Default to other if no key is provided
      const key = attr[0].toLowerCase() || "other";
      const level = attr[1];
      const label = attr[2];
      if (!status[key]) {
        status[key] = [];
      }
      status[key].push({
        label: label,
        level: level
      });
    });
  }
  return status;
}

export function createAllergies(patient: Patient | null) {
  if (patient) {
    const allergies = _.get(patient, "body.atoms.allergies", null);
    if (allergies) {
      const allergyList = allergies.legend.split("|");

      const list = allergyList.map((allergy: any) => {
        return {
          label: allergy,
          // No value means no known allergies so should be gray bullet for level
          level: allergies.level
        };
      });
      return {
        list,
        level: allergies.level,
        value: allergies.value
      };
    }
  }
  return { list: [], value: 0 };
}

function createLevels(level: any, max: number) {
  const levels: Array<any> = [];
  for (let i = 1; i < max; i++) {
    levels.push(level[`level${i}`]);
  }
  levels.push(level[`level${max - 1}`]);
  const mapped = levels.map((item, key): any => {
    if (item != Number.MAX_VALUE && item != -1 * Number.MAX_VALUE) {
      const inverted = level.invert;
      const invertMultiplier = inverted ? -1.0 : 1.0;
      const last = levels.length - 1 === key;
      let sign = "";

      if (inverted) {
        if (last) {
          sign = "≤";
        } else {
          sign = ">";
        }
      } else {
        if (last) {
          sign = "≥";
        } else {
          sign = "<";
        }
      }
      return {
        value: item * invertMultiplier,
        sign: sign
      };
    } else {
      if (
        key >= 2 &&
        item === Number.MAX_VALUE &&
        levels[key - 1] !== Number.MAX_VALUE
      ) {
        return { value: levels[key - 1], sign: "≥" };
      } else {
        return { value: "", sign: "" };
      }
    }
  });
  const numberOfEmpties = max - mapped.length;
  for (let j = 0; j < numberOfEmpties; j++) {
    mapped.push({ value: "", sign: "" });
  }
  return mapped;
}

function getVarsForCurrentArea(state: PatientState, area: string) {
  if (state.vars && state.vars[area]) {
    return state.vars[area];
  } else {
    return null;
  }
}

function getCurrentPatient(state: PatientState) {
  if (state.activePatientId) {
    const patient = state.patientList.filter(
      p => p.id === state.activePatientId
    );
    if (patient.length === 1 && patient[0]) {
      return patient[0];
    }
  }
  return null;
}
export const getters: GetterTree<PatientState, RootState> = {
  now(state) {
    return state.stats.now;
  },
  stats(state) {
    return state.stats;
  },
  hasError(state): boolean {
    return !!state.error;
  },
  patients(state): Array<Patient> {
    return state.patientList;
  },
  getById(state): any {
    return (id: number): any => {
      const patient = state.patientList.filter(p => p.id === id);
      if (patient.length === 1) {
        return patient[0];
      }
      return null;
    };
  },
  graph(state): any {
    return (
      keys:
        | Array<string>
        | Array<{ key: string; label: string; yAxisID: string }>
        | string,
      filterEmpty = false
    ) => {
      if (typeof keys === "string") {
        keys = [keys];
      }
      // if only a list of keys was passed, use the key as the label
      const keysWithLabels = (keys as Array<any>).map(key => {
        if (typeof key === "string") {
          return { key, label: key };
        } else {
          return key;
        }
      });

      const currentPatient = getCurrentPatient(state);
      if (currentPatient && currentPatient.body && currentPatient.body.graph) {
        const graph = currentPatient.body.graph;
        if (graph) {
          return keysWithLabels
            .map((keyLabel: any) => {
              const data = graph[keyLabel.key]
                ? convertServerDataPoints(graph[keyLabel.key])
                : [];
              return {
                label: keyLabel.label,
                yAxisID: keyLabel.yAxisID,
                data
              };
            })
            .filter(item => {
              if (filterEmpty) {
                return item.data.length !== 0;
              } else {
                return true;
              }
            });
        }
      }
      return [];
    };
  },
  segmentRisk(state, getters): any {
    return (...args: any[]) => {
      if (state.activePatientId) {
        const patient = getCurrentPatient(state);
        if (patient && patient.body) {
          const body = patient.body;
          const vars = getVarsForCurrentArea(state, getters.area);
          if (body && vars && vars.riskMap) {
            let mappedRisks: Array<any> = [];
            // Handle both forms of arguments:
            // segmentRisk([['brain', 'spine'], ['brain', 'both']])
            // segmentRisk('brain', 'spine')
            if (args.length === 2) {
              args = [[args[0], args[1]]];
            } else {
              args = args[0];
            }
            args.forEach((combo: any) => {
              const [part, segment] = combo;
              const risks = vars.riskMap[part][segment];
              if (!risks) {
                return null;
              }

              const thisSection = risks.map((r: RiskFactor) => {
                const atRisk = body.risk.includes(r.name);
                let level = 0;
                if (atRisk) {
                  if (r.weight === 0) {
                    level = 2;
                  } else if (r.weight && r.weight < 0) {
                    level = 3;
                  } else {
                    level = 5;
                  }
                }
                return {
                  label: r.display,
                  level
                };
              });
              mappedRisks = mappedRisks.concat(thisSection);
            });

            let noRisk = mappedRisks.every((risk: any) => risk.level === 0);
            if (noRisk) {
              mappedRisks.push({
                label: "No risk factors documented",
                level: 3
              });
            }
            return mappedRisks;
          }
        }
      }
      return null;
    };
  },
  atRisk(state): any {
    return (risk: string) => {
      if (state.activePatientId) {
        const patient = getCurrentPatient(state);
        if (patient && patient.body) {
          return patientHasRisk(patient, risk);
        }
      }
      return false;
    };
  },
  current(state) {
    return getCurrentPatient(state);
  },
  currentRoomName(state) {
    const pt = getCurrentPatient(state);
    return pt !== null ? pt.census.room : "";
  },
  references(state, getters) {
    if (!getVarsForCurrentArea(state, getters.area)) {
      return null;
    }
    return getVarsForCurrentArea(state, getters.area).referencesMap;
  },
  editLimitsConfig(state, getters) {
    if (!getVarsForCurrentArea(state, getters.area)) {
      return null;
    }
    return getVarsForCurrentArea(state, getters.area).editLimitsConfig;
  },
  minDataPatient(_state, getters) {
    return _.get(getters, "current.census.minDataPatient", false);
  },
  orderSetConfig(state, getters) {
    if (!getVarsForCurrentArea(state, getters.area)) {
      return null;
    }
    return getVarsForCurrentArea(state, getters.area).orderSetConfig;
  },

  alerts(state, getters) {
    if (!getVarsForCurrentArea(state, getters.area)) {
      return null;
    }
    return getVarsForCurrentArea(state, getters.area).alertRules;
  },
  allVars(state) {
    return state.vars;
  },
  vars(state, getters) {
    return getVarsForCurrentArea(state, getters.area);
  },
  caseState(state, getters) {
    if (!getVarsForCurrentArea(state, getters.area)) {
      return null;
    }
    return getVarsForCurrentArea(state, getters.area).caseState;
  },
  displayVarLevels(state, getters) {
    if (!getVarsForCurrentArea(state, getters.area)) {
      return [];
    }
    // TODO: Rethink how we interact with state, should not need to clone it like this
    const levels: Array<VarLevel> | null = _.cloneDeep(
      getVarsForCurrentArea(state, getters.area).varLevels
    );
    let currentCategory = "";

    if (levels) {
      return levels
        .filter(f => f.title !== "") // TODO: This removes TidalVolume and MAP (Mean Airway Pressure?) because these are pt specific.
        .map((level: any) => {
          if (currentCategory === level.category) {
            level.category = "";
          } else {
            currentCategory = level.category;
          }
          level.levels = createLevels(level, 5);
          return level;
        });
    }
    return [];
  },
  atomList(state, getters) {
    if (!getVarsForCurrentArea(state, getters.area)) {
      return null;
    }
    return getVarsForCurrentArea(state, getters.area).atomList;
  },
  mobileDisplayVarLevels(state, getters) {
    if (!getVarsForCurrentArea(state, getters.area)) {
      return [];
    }
    // TODO: Rethink how we interact with state, should not need to clone it like this
    const levels: Array<VarLevel> | null = _.cloneDeep(
      getVarsForCurrentArea(state, getters.area).varLevels
    );
    let currentCategory = "";

    if (levels) {
      return levels
        .filter(f => f.title !== "") // TODO: This removes TidalVolume and MAP (Mean Airway Pressure?) because these are pt specific.
        .map((level: any) => {
          level.levels = createLevels(level, 5);
          return level;
        });
    }
    return [];
  },
  units(state, getters) {
    if (
      getVarsForCurrentArea(state, getters.area) &&
      getVarsForCurrentArea(state, getters.area).units
    ) {
      const serverData = getVarsForCurrentArea(state, getters.area).units;
      const unitData = getters.config.census.customUnits.concat(serverData);
      return unitData
        .filter((unit: any) => {
          // type 0 are not selectable
          return unit.type !== 0;
        })
        .map((unit: any) => {
          return {
            value: unit.unit,
            text: unit.label
          };
        });
    } else {
      return [];
    }
  },
  selectedFilters(_state, _getters, rootState) {
    if (rootState.route.params.filters) {
      return rootState.route.params.filters;
    } else {
      return null;
    }
  },
  selectedSort(_state, _getters, rootState) {
    if (rootState.route.params.sort) {
      return rootState.route.params.sort;
    } else {
      return SORT_BY_ROOM;
    }
  },
  selectedUnit(_state, getters, rootState) {
    const units = getters.units;
    if (units && units.length) {
      if (
        rootState.route.params.unit &&
        units
          .map((unit: any) => {
            return unit.value;
          })
          .includes(rootState.route.params.unit)
      ) {
        return rootState.route.params.unit;
      } else {
        return units[0].value;
      }
    } else {
      return null;
    }
  },
  allergies(state, _getters) {
    const currentPatient = getCurrentPatient(state);
    return createAllergies(currentPatient);
  },
  area(_state, _getters, rootState) {
    return rootState.route.params.area;
  },
  selectedView(_state, _getters, rootState) {
    return rootState.route.params.view;
  },
  config(state, getters) {
    return state.configs[getters.area];
  },
  hasWaves(_state, getters) {
    return _.get(getters.current, "census.hasWaves", false);
  },
  showStaff(_state, getters) {
    return (
      _.get(getters.current, "census.staff", []).length > 0 ||
      _.get(getters.current, "census.phone", "") !== ""
    );
  },
  viewConfig(_state, getters) {
    const selectedView = getters.selectedView;
    const views = getters.views;
    const index = views.map((v: any) => v.routerString).indexOf(selectedView);
    return views[index];
  },
  views(_state, getters): ViewConfig[] {
    const censusViews = _.get(getters, "vars.uiConfig.censusViews", []);
    const censusTowerViews = _.get(
      getters,
      "vars.uiConfig.censusTowerViews",
      []
    );
    return getters.towerMode ? censusTowerViews : censusViews;
  },
  showMedData(_state, getters): boolean {
    const showMedData = _.get(
      getters,
      "vars.uiConfig.elements.showMedData",
      false
    );
    return showMedData;
  },
  careCoordinationNote(state) {
    return state.careNote;
  },
  medList(state) {
    return state.medList;
  },
  infusions(_state, getters) {
    const currentPatient = getters.current;
    return createInfusions(currentPatient);
  },
  changeLog(_state, getters) {
    return _.get(getters, "vars.changeLog.changes", {});
  },
  mapAlert(_state, getters) {
    return _.get(getters, "current.body.current.BPRed", null);
  },
  popupDescription(_state, getters, rootState): PopupDescription | null {
    const popupName = rootState.route.query.popupName;
    const descriptions = _.get(getters, "config.popups", {}) as Record<
      string,
      PopupDescription
    >;
    // Default to using name as component with replace as default
    const defaultConfig = {
      component: popupName,
      location: LocationType.replace
    };

    // Let each area provide additional defaults
    const areaConfig = {
      popupConfig: _.get(getters, "config.defaultPopupDescription", {})
    };

    // Individual popups can provide even more detailed overrides
    const popupDescription = descriptions[popupName];

    return _.merge(defaultConfig, areaConfig, popupDescription);
  },
  audioAlerts(state) {
    return state.audioAlerts;
  },
  singlePatientView(state) {
    return state.singlePatientView;
  },
  verifyMode(state) {
    return state.verifyMode;
  },
  serverTowerMode(state, getters) {
    return _.get(getters, "vars.system.controlTower") === "yes";
  },
  towerMode(state, getters) {
    const towerSetByServer =
      _.get(getters, "vars.system.controlTower") === "yes";
    return state.towerMode && towerSetByServer;
  },
  towerChoices(_state, getters) {
    return _.get(getters, "vars.actionPick", []);
  },
  towerChoicesIndexed(_state, getters) {
    const vars = getters.vars;
    if (!vars || !vars.actionPick) {
      return [];
    }
    const choices: Record<string, string> = {};
    Object.keys(vars.actionPick).forEach(key => {
      vars.actionPick[key].forEach((o: any) => {
        choices[`${o.id}.${o.seq}`] = o.option;
      });
    });
    return choices;
  },
  towerAlertPick(_state, getters): Record<string, string> {
    const vars = getters.vars;
    if (!vars || !vars.alertRules) {
      return {};
    }
    const alertPick: Record<string, string> = {};
    vars.alertRules.forEach((alertRule: any) => {
      if (alertRule.id && alertRule.pickID) {
        alertPick[alertRule.id] = alertRule.pickID;
      }
    });
    return alertPick;
  },
  isTesting(state): boolean {
    return state.isTesting;
  },
  isPagingTest(state): boolean {
    return state.isPagingTest;
  },

  isPagingTestActive(state): boolean {
    return state.isPagingTestActive;
  },
  isActiveTest(state): boolean {
    return state.isActiveTesting;
  },
  censusLoaded(state): boolean {
    return state.censusLoaded;
  },
  varsLoaded(state): boolean {
    return state.varsLoaded;
  }
};
